import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { HashLoader } from "react-spinners";
import { useAuth } from "../../lib/context/AuthContext";
import { useTrips } from "../../lib/context/TripsContext";
import useBnplGate from "../../lib/hooks/useBnplGate";
import CompleteSetupTaskList, {
  useStartSetupTask,
} from "../../lib/components/CompleteSetupTaskList";
import BnplSetupGateDialog from "../../lib/components/BnplSetupGateDialog";
import AviationBnplPayment from "./components/AviationBnplPayment";
import TripsBookingIframe from "./components/TripsBookingIframe";

const THIS_PATH = "/aviation/flights";
const RESUME_STATE = { resumeBnplGate: true };

/**
 * Aviation booking via Trips iframe, then Visaro BNPL widget for payment.
 * Route: <Route path="flights" element={<AviationFlightsPage />} /> under aviation.
 */
const AviationFlightsPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const startSetupTask = useStartSetupTask();
  const [showSdkCheckout, setShowSdkCheckout] = useState(false);
  const {
    isBnplAllowed,
    isCheckingBnplAccess,
    firstTaskPending,
    pendingTasks,
    isGateOpen,
    openGate,
    closeGate,
    isSetupListOpen,
    openSetupList,
    closeSetupList,
  } = useBnplGate();

  // Back from a setup task started at the gate: re-check once loaded.
  const resumingBnplGate = !!(
    location.state as { resumeBnplGate?: boolean } | null
  )?.resumeBnplGate;

  const {
    flightData,
    submitingNewFlightData,
    submitNewFlightDataError,
    initializingNewFlight,
    bookingURL,
  } = useTrips();

  // Once per mount; see SelectPaymentMethod.
  const resumeChecked = useRef(false);
  useEffect(() => {
    if (!resumingBnplGate || !flightData || isCheckingBnplAccess) return;
    if (resumeChecked.current) return;
    resumeChecked.current = true;
    if (!isBnplAllowed) openGate();
  }, [resumingBnplGate, flightData, isCheckingBnplAccess, isBnplAllowed, openGate]);

  const sdkReady = useMemo(() => {
    return !!flightData && !submitingNewFlightData && !submitNewFlightDataError;
  }, [flightData, submitingNewFlightData, submitNewFlightDataError]);

  const showPayment = sdkReady && showSdkCheckout;

  useEffect(() => {
    if (!sdkReady) {
      setShowSdkCheckout(false);
    }
  }, [sdkReady]);

  const handlePaymentClose = () => {
    navigate("/aviation/your-flights", { replace: false });
  };

  if (submitNewFlightDataError) {
    return (
      <div className="min-h-[50vh] flex flex-col items-center justify-center gap-4 p-6 text-center">
        <p className="text-gray-800 font-medium">Could not save your flight booking</p>
        <p className="text-sm text-gray-600 max-w-md">
          Please try again or use the standard checkout flow.
        </p>
        <Link
          to="/aviation/select-payment-method"
          className="text-visaro-primary-700 font-medium underline"
        >
          Continue to payment options
        </Link>
      </div>
    );
  }

  return (
    <div className="relative min-h-screen w-full">
      <BnplSetupGateDialog
        isOpen={isGateOpen}
        onClose={closeGate}
        firstTaskPending={firstTaskPending}
        pendingTasks={pendingTasks}
        onStartTask={(task) => {
          closeGate();
          startSetupTask(task, {
            returnTo: THIS_PATH,
            returnState: RESUME_STATE,
            bookingId: flightData?.id || undefined,
          });
        }}
        onViewAllSteps={openSetupList}
      />
      <CompleteSetupTaskList
        isOpen={isSetupListOpen}
        handleClose={closeSetupList}
        title="Finish setup to pay in installments"
        subtitle="Complete these steps to unlock installments. Your selected flight is kept while you do."
        returnTo={THIS_PATH}
        returnState={RESUME_STATE}
        bookingId={flightData?.id || undefined}
      />

      {(submitingNewFlightData || initializingNewFlight) && (
        <div className="fixed inset-0 z-[150] flex items-center justify-center bg-white/70 backdrop-blur-sm">
          <HashLoader />
        </div>
      )}

      {!showPayment && !sdkReady && (
        <>
          <div className="px-4 pt-4 pb-2 border-b border-gray-100 bg-white">
            <h1 className="text-lg font-semibold text-gray-900">Local flights</h1>
            <p className="text-sm text-gray-600 mt-1">
              Domestic routes in Nigeria — same Trips search as &quot;New local flight&quot;;
              pay with Visaro BNPL at checkout.
            </p>
          </div>
          {!bookingURL && !initializingNewFlight && (
            <div className="p-4 text-sm text-gray-600">
              Preparing flight search…
            </div>
          )}
          <TripsBookingIframe domesticOnly />
        </>
      )}

      {!showPayment && sdkReady && (
        <div className="mx-auto mt-10 max-w-2xl rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
          <h2 className="text-lg font-semibold text-gray-900">Flight selected</h2>
          <p className="mt-2 text-sm text-gray-600">
            Your flight booking has been initialized. Continue when you are ready to open
            the BNPL SDK checkout.
          </p>
          <div className="mt-5 flex flex-wrap gap-3">
            <button
              type="button"
              onClick={() => {
                // BNPL checkout is the gated step — browsing and selecting a
                // flight is open to everyone.
                if (!isBnplAllowed) {
                  if (isCheckingBnplAccess) return;
                  openGate();
                  return;
                }
                setShowSdkCheckout(true);
              }}
              className="rounded-md bg-visaro-primary-700 px-4 py-2 text-sm font-medium text-white"
            >
              Show SDK checkout
            </button>
            <button
              type="button"
              onClick={handlePaymentClose}
              className="rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700"
            >
              Back to tickets
            </button>
          </div>
        </div>
      )}

      {showPayment && flightData && (
        <AviationBnplPayment
          flight={flightData}
          customerEmail={user?.email}
          onClose={handlePaymentClose}
        />
      )}
    </div>
  );
};

export default AviationFlightsPage;
